/**
 * dungeon.ts — the arena's dressing: flagstones, masonry, vaulting and torches.
 *
 * Everything here is generated from the shared ARENA description, the same one
 * the server builds its Rapier colliders from, so the room you can see and the
 * room you can walk in are the same room.
 *
 * The one rule that governs every decision below: NOTHING DECORATIVE INTRUDES
 * INTO PLAYABLE SPACE. Masonry is built flush with the wall face and grows
 * outward; only the torch sconces stand proud, and they sit at 2.9m, above
 * anything a duellist can reach.
 */

import * as THREE from 'three';
import { ARENA } from '@dueler/shared';
import { createIronMaterial, createStoneMaterial, markNearDetail } from './surfaces.js';

export { RELIEF as MORTAR_RELIEF };

export interface Dungeon {
  object: THREE.Object3D;
  /** Torch flicker. `now` is render time, so it holds still during hitstop. */
  update(now: number): void;
  disposables: { dispose(): void }[];
}

/** Deterministic hash noise, so the room looks the same every time you load it. */
function hash(n: number): number {
  const x = Math.sin(n * 127.1) * 43758.5453;
  return x - Math.floor(x);
}

/** Larger slabs than before — fewer instances, more geometry per stone. */
const FLAGSTONE = 2.4;
const JOINT = 0.08;

const BLOCK_W = 1.8;
const BLOCK_H = 0.72;
/**
 * How far the mortar backing is set BEHIND the masonry, m.
 * Blocks sit flush with the collider face; mortar retreats from it.
 */
const RELIEF = 0.035;

const TORCH_HEIGHT = 2.9;

export function createDungeon(): Dungeon {
  const root = new THREE.Group();
  const disposables: { dispose(): void }[] = [];
  const track = <T extends { dispose(): void }>(item: T): T => {
    disposables.push(item);
    return item;
  };

  const half = ARENA.size / 2;
  const color = new THREE.Color();
  const matrix = new THREE.Matrix4();
  const quat = new THREE.Quaternion();
  const pos = new THREE.Vector3();
  const scale = new THREE.Vector3();
  // ---- mortar bed ----
  const bedMat = track(
    new THREE.MeshStandardMaterial({ color: 0x1a1610, roughness: 1, metalness: 0 }),
  );
  const bedGeo = track(new THREE.PlaneGeometry(ARENA.size, ARENA.size, 1, 1));
  // Soft height noise in the mortar plane so joints are not laser-flat.
  {
    const attr = bedGeo.attributes['position'] as THREE.BufferAttribute;
    for (let i = 0; i < attr.count; i += 1) {
      const x = attr.getX(i);
      const y = attr.getY(i);
      attr.setZ(i, (hash(x * 17 + y * 31) - 0.5) * 0.02);
    }
    bedGeo.computeVertexNormals();
  }
  const bed = new THREE.Mesh(bedGeo, bedMat);
  bed.rotation.x = -Math.PI / 2;
  bed.receiveShadow = true;
  root.add(bed);

  // ---- flagstone floor (dense, textured; thin enough that physics stays flat) ----
  const perSide = Math.floor(ARENA.size / FLAGSTONE);
  const stoneGeo = track(new THREE.BoxGeometry(FLAGSTONE - JOINT, 0.055, FLAGSTONE - JOINT, 2, 1, 2));
  // Soft top chamfer via vertex push — visual bevel without growing the AABB.
  {
    const attr = stoneGeo.attributes['position'] as THREE.BufferAttribute;
    for (let v = 0; v < attr.count; v += 1) {
      if (attr.getY(v) > 0.01) {
        const x = attr.getX(v);
        const z = attr.getZ(v);
        const edge = Math.max(Math.abs(x) / ((FLAGSTONE - JOINT) / 2), Math.abs(z) / ((FLAGSTONE - JOINT) / 2));
        if (edge > 0.85) attr.setY(v, attr.getY(v) * (1 - (edge - 0.85) * 0.8));
      }
    }
    stoneGeo.computeVertexNormals();
  }
  stoneGeo.setAttribute('uv2', stoneGeo.getAttribute('uv').clone());
  const stoneMat = track(createStoneMaterial(0x8b8172));
  const floor = new THREE.InstancedMesh(stoneGeo, stoneMat, perSide * perSide);
  floor.instanceMatrix.setUsage(THREE.StaticDrawUsage);
  // Receive only — hundreds of instanced stones casting into the soft map
  // dominated shadow cost after the fidelity pass.
  floor.castShadow = false;
  floor.receiveShadow = true;

  let i = 0;
  for (let gx = 0; gx < perSide; gx += 1) {
    for (let gz = 0; gz < perSide; gz += 1) {
      const n = hash(gx * 37 + gz * 91);
      const m = hash(gx * 13 + gz * 7 + 5);
      pos.set(
        -half + FLAGSTONE * (gx + 0.5),
        0.028 - m * 0.01,
        -half + FLAGSTONE * (gz + 0.5),
      );
      quat.setFromAxisAngle(UP, (n - 0.5) * 0.04);
      scale.set(1, 1, 1);
      floor.setMatrixAt(i, matrix.compose(pos, quat, scale));

      const damp = Math.min(1, (Math.hypot(pos.x, pos.z) / half) ** 2);
      color
        .setHex(0x8b8172)
        .lerp(TONE_DARK, n * 0.55)
        .lerp(TONE_MOSS, damp * m * 0.35);
      floor.setColorAt(i, color);
      i += 1;
    }
  }
  floor.instanceMatrix.needsUpdate = true;
  floor.name = 'flagstones';
  root.add(floor);
  disposables.push({ dispose: () => floor.dispose() });

  // ---- masonry walls ----
  const courses = Math.ceil(ARENA.wallHeight / BLOCK_H);
  const perCourse = Math.ceil(ARENA.size / BLOCK_W) + 1;
  // Dense box flush with the collider face, extending outward only.
  const wallBlockGeo = track(
    new THREE.BoxGeometry(BLOCK_W - 0.05, BLOCK_H - 0.05, ARENA.wallThickness, 1, 1, 1),
  );
  wallBlockGeo.setAttribute('uv2', wallBlockGeo.getAttribute('uv').clone());
  const blockMat = track(createStoneMaterial(0x7b7364));
  const walls = new THREE.InstancedMesh(wallBlockGeo, blockMat, courses * perCourse * 4);
  walls.instanceMatrix.setUsage(THREE.StaticDrawUsage);
  walls.castShadow = false;
  walls.receiveShadow = true;

  let w = 0;
  const SIDES = [
    { yaw: 0, nx: 0, nz: 1 },
    { yaw: Math.PI, nx: 0, nz: -1 },
    { yaw: Math.PI / 2, nx: 1, nz: 0 },
    { yaw: -Math.PI / 2, nx: -1, nz: 0 },
  ] as const;

  for (let s = 0; s < SIDES.length; s += 1) {
    const side = SIDES[s]!;
    quat.setFromAxisAngle(UP, side.yaw);
    for (let c = 0; c < courses; c += 1) {
      const stagger = (c % 2) * (BLOCK_W / 2);
      for (let b = 0; b < perCourse; b += 1) {
        const along = -half - BLOCK_W / 2 + stagger + b * BLOCK_W;
        if (along < -half - BLOCK_W || along > half + BLOCK_W) continue;
        const depth = half + ARENA.wallThickness / 2;
        pos.set(
          side.nz === 0 ? -side.nx * depth : along,
          BLOCK_H * (c + 0.5),
          side.nz === 0 ? along : -side.nz * depth,
        );
        floorClamp(pos, half + ARENA.wallThickness);
        walls.setMatrixAt(w, matrix.compose(pos, quat, ONE));

        const n = hash(s * 311 + c * 53 + b * 17);
        color.setHex(0x7b7364).lerp(TONE_DARK, n * 0.6);
        walls.setColorAt(w, color);
        w += 1;
      }
    }
  }
  walls.count = w;
  walls.instanceMatrix.needsUpdate = true;
  walls.name = 'masonry';
  root.add(walls);
  disposables.push({ dispose: () => walls.dispose() });

  // ---- vaulting ----
  const ceilMat = track(
    new THREE.MeshStandardMaterial({
      color: 0x272218,
      roughness: 1,
      metalness: 0,
      side: THREE.DoubleSide,
    }),
  );
  const ceilGeo = track(
    new THREE.PlaneGeometry(
      ARENA.size + ARENA.wallThickness * 2,
      ARENA.size + ARENA.wallThickness * 2,
      1,
      1,
    ),
  );
  const ceiling = new THREE.Mesh(ceilGeo, ceilMat);
  ceiling.rotation.x = Math.PI / 2;
  ceiling.position.y = ARENA.wallHeight;
  ceiling.receiveShadow = true;
  root.add(ceiling);

  const ribMat = track(createStoneMaterial(0x4f4839));
  const ribGeo = track(new THREE.BoxGeometry(ARENA.size, 0.28, 0.5, 1, 1, 1));
  for (let r = -2; r <= 2; r += 1) {
    const rib = new THREE.Mesh(ribGeo, ribMat);
    rib.position.set(0, ARENA.wallHeight - 0.16, r * (ARENA.size / 5));
    rib.castShadow = false;
    rib.receiveShadow = true;
    root.add(rib);
  }

  // ---- wrought-iron sconces + flame volumes ----
  const torches: Torch[] = [];
  const iron = track(createIronMaterial());
  const bracketArmGeo = track(new THREE.CylinderGeometry(0.018, 0.022, 0.38, 8));
  const cupGeo = track(new THREE.CylinderGeometry(0.055, 0.04, 0.1, 8));
  const ringGeo = track(new THREE.TorusGeometry(0.06, 0.008, 6, 12));
  const flameGeo = track(new THREE.ConeGeometry(0.09, 0.28, 8, 1, true));
  const glowGeo = track(new THREE.SphereGeometry(0.07, 8, 6));

  for (const side of SIDES) {
    for (const along of [-half * 0.5, half * 0.5]) {
      const x = side.nz === 0 ? -side.nx * (half - 0.06) : along;
      const z = side.nz === 0 ? along : -side.nz * (half - 0.06);

      const mount = new THREE.Group();
      mount.position.set(x + side.nx * 0.08, TORCH_HEIGHT - 0.1, z + side.nz * 0.08);

      const plate = new THREE.Mesh(
        track(new THREE.BoxGeometry(0.12, 0.18, 0.03, 1, 1, 1)),
        iron,
      );
      plate.position.set(-side.nx * 0.02, 0, -side.nz * 0.02);
      plate.castShadow = false;
      mount.add(plate);

      const arm = new THREE.Mesh(bracketArmGeo, iron);
      arm.rotation.set(side.nz * 0.9, 0, -side.nx * 0.9);
      arm.position.set(side.nx * 0.12, 0.02, side.nz * 0.12);
      arm.castShadow = false;
      mount.add(arm);

      const cup = new THREE.Mesh(cupGeo, iron);
      cup.position.set(side.nx * 0.22, 0.12, side.nz * 0.22);
      cup.castShadow = false;
      mount.add(cup);

      const ring = new THREE.Mesh(ringGeo, iron);
      ring.rotation.x = Math.PI / 2;
      ring.position.copy(cup.position);
      ring.position.y += 0.05;
      markNearDetail(ring);
      mount.add(ring);

      root.add(mount);

      const flameMat = track(
        new THREE.MeshBasicMaterial({
          color: 0xffb060,
          transparent: true,
          opacity: 0.88,
          depthWrite: false,
          side: THREE.DoubleSide,
        }),
      );
      const flame = new THREE.Mesh(flameGeo, flameMat);
      flame.position.set(x + side.nx * 0.22, TORCH_HEIGHT + 0.22, z + side.nz * 0.22);
      root.add(flame);

      const glowMat = track(
        new THREE.MeshBasicMaterial({
          color: 0xff9020,
          transparent: true,
          opacity: 0.45,
          depthWrite: false,
        }),
      );
      const glow = new THREE.Mesh(glowGeo, glowMat);
      glow.position.copy(flame.position);
      glow.position.y -= 0.06;
      root.add(glow);

      // Point-light cubemap shadows (6 faces each) were the other big frame
      // killer. Torches light the room; the grate shaft casts the readable shadow.
      const light = new THREE.PointLight(0xffc074, 42, 32, 1.75);
      light.position.copy(flame.position);
      light.castShadow = false;
      root.add(light);

      torches.push({ light, flame, glow, seed: torches.length * 12.9898, base: 42 });
    }
  }

  return {
    object: root,
    disposables,
    update(now) {
      const t = now / 1000;
      for (const torch of torches) {
        const flick =
          0.94 +
          0.035 * Math.sin(t * 9.1 + torch.seed) +
          0.02 * Math.sin(t * 16.3 + torch.seed * 2.3) +
          0.015 * Math.sin(t * 2.7 + torch.seed * 0.7);
        torch.light.intensity = torch.base * flick;
        const shape = 1 + (flick - 0.94) * 2.2;
        torch.flame.scale.set(0.9 * shape, 1.35 * shape, 0.9 * shape);
        torch.glow.scale.setScalar(0.85 + (flick - 0.94) * 1.5);
      }
    },
  };
}

interface Torch {
  light: THREE.PointLight;
  flame: THREE.Mesh;
  glow: THREE.Mesh;
  seed: number;
  base: number;
}

const UP = new THREE.Vector3(0, 1, 0);
const ONE = new THREE.Vector3(1, 1, 1);
const TONE_DARK = new THREE.Color(0x413a2e);
const TONE_MOSS = new THREE.Color(0x3c4a2c);

function floorClamp(p: THREE.Vector3, limit: number): void {
  p.x = Math.max(-limit, Math.min(limit, p.x));
  p.z = Math.max(-limit, Math.min(limit, p.z));
}
